import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';

/**
 * Growth › Find a user.
 *
 * Deliberately a search box and nothing else. The product doc's
 * aggregate-by-default rule (Section 7) means there is no browsable list of
 * people here — you arrive knowing who you are looking for, usually from a
 * support ticket, and opening their page is itself written to the audit log.
 * A default list of users would turn a deliberate action into idle browsing.
 *
 * Gated to operator/support/owner in the nav, the router and the API. An
 * analyst never sees this tab.
 */
export default function PeoplePage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (q.length < 3) {
      toast.error('Enter at least three characters');
      return;
    }
    setSearching(true);
    try {
      const { data } = await adminApi.get('/users/search', { params: { q } });
      setResults(data.items || []);
    } catch (err) {
      toast.error(errMsg(err, 'Search failed'));
    } finally {
      setSearching(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Find a user"
        hint="For support lookups. Opening someone's page is recorded in the audit log with your name on it."
      />

      <form onSubmit={search} className="mb-6 flex max-w-xl gap-2">
        <input
          autoFocus
          placeholder="Phone number or name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 rounded-lg border border-hairline px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300"
        />
        <button
          type="submit"
          disabled={searching}
          className="rounded-lg bg-raspberry-600 px-4 py-2 text-sm font-medium text-white hover:bg-raspberry-700 disabled:opacity-50"
        >
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>

      {results == null ? (
        <EmptyState
          title="Search for someone"
          detail="Phone matches exactly; name matches on any part of it. Nothing is listed until you ask for it — that is the point."
        />
      ) : results.length === 0 ? (
        <EmptyState title="No matches" detail="Try the full phone number, including or excluding the country code." />
      ) : (
        <div className="max-w-xl divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-surface shadow-sm">
          {results.map((u) => (
            <button
              key={u.user_id}
              onClick={() => navigate(`/user/${encodeURIComponent(u.user_id)}`)}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-surface-page"
            >
              <span className="min-w-0">
                <span className="block text-sm font-medium text-ink">{u.name || '(no name)'}</span>
                <span className="block truncate font-mono text-[11px] text-ink-muted">
                  {u.phone} · {u.user_id}
                </span>
              </span>
              <span className="flex-shrink-0 text-xs font-medium text-raspberry-600">Open →</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
